import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { useAppSelector } from "../hooks";
import type { RootState } from "../store";

export interface Charge {
  quantity: number;
  name: string;
  amount: number;
}

export interface PromotionState {
  promotionCode: string;
  isValid: boolean | null;
  discountAmount: number;
  discountCharges: Charge[];
  hasPromotionInBooking: boolean;
  promoValidateInfo: string;
}

const initialState: PromotionState = {
  promotionCode: "",
  isValid: null,
  discountAmount: 0,
  discountCharges: [],
  hasPromotionInBooking: false,
  promoValidateInfo: "",
};

const promotionSlice = createSlice({
  name: "promotion",
  initialState,
  reducers: {
    setPromotionCode: (state, action: PayloadAction<string>) => {
      state.promotionCode = action.payload;
    },
    setPromotionValidity: (state, action: PayloadAction<boolean>) => {
      state.isValid = action.payload;
    },
    setPromotionDiscountAmount: (state, action: PayloadAction<number>) => {
      if (state.discountAmount === action.payload) return;
      state.discountAmount = action.payload;
    },
    setPromotionDiscountCharges: (state, action: PayloadAction<Charge[]>) => {
      if (state.discountCharges === action.payload) return;
      state.discountCharges = action.payload;
    },
    setPromoValidateInfo: (state, action: PayloadAction<string>) => {
      state.promoValidateInfo = action.payload;
    },
    resetPromotion: (state) => {
      state.promotionCode = "";
      state.isValid = null;
      state.discountAmount = 0;
      state.discountCharges = [];
      state.promoValidateInfo = "";
      state.hasPromotionInBooking = false;
    },
    resetAndPreserveBookingPromotion: (state) => {
      state.promotionCode = "";
      state.isValid = null;
      state.discountAmount = 0;
      state.discountCharges = [];
      state.promoValidateInfo = "";
    },
    setHasPromotionInBooking: (state, action: PayloadAction<boolean>) => {
      state.hasPromotionInBooking = action.payload;
    },
  },
});

// Actions
export const {
  setPromotionCode,
  setPromotionValidity,
  setPromotionDiscountAmount,
  setPromotionDiscountCharges,
  setPromoValidateInfo,
  resetPromotion,
  resetAndPreserveBookingPromotion,
  setHasPromotionInBooking,
} = promotionSlice.actions;

// Selectors
export const selectPromotionCode = (state: RootState) => state.promotion.promotionCode;
export const selectPromoIsValid = (state: RootState) => state.promotion.isValid;
export const selectPromoDiscountAmount = (state: RootState) => state.promotion.discountAmount;
export const selectPromoDiscountCharges = (state: RootState) => state.promotion.discountCharges;
export const selectPromoValidateInfo = (state: RootState) => state.promotion.promoValidateInfo;
export const selectHasPromotionInBooking = (state: RootState) =>
  state.promotion.hasPromotionInBooking;

// Hooks
export const usePromotionState = () => useAppSelector((s) => s.promotion);
export const usePromotionCode = () => useAppSelector((s) => s.promotion.promotionCode);
export const usePromotionIsValid = () => useAppSelector((s) => s.promotion.isValid);
export const usePromotionDiscountAmount = () => useAppSelector((s) => s.promotion.discountAmount);
export const usePromotionDiscountCharges = () => useAppSelector((s) => s.promotion.discountCharges);
export const usePromoValidateInfo = () => useAppSelector((s) => s.promotion.promoValidateInfo);
export const useHasPromotionInBooking = () =>
  useAppSelector((s) => s.promotion.hasPromotionInBooking);

export default promotionSlice.reducer;
